import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, Observable, of, switchMap, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DashboardResponse, RequirementsFunnelResponse } from '../models/dashboard.model';

const POLL_INTERVAL_MS = 30000;

@Injectable({ providedIn: 'root' })
export class DashboardDataService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiBaseUrl}/api/v1/dashboard`;

  getDashboard(von?: string, bis?: string, projectIds: string[] = []): Observable<DashboardResponse> {
    let params = withProjectIds(new HttpParams(), projectIds);
    if (von) params = params.set('von', von);
    if (bis) params = params.set('bis', bis);
    return this.http.get<DashboardResponse>(this.apiUrl, { params });
  }

  /**
   * `catchError` sitzt bewusst innerhalb des `switchMap`, damit ein einzelner
   * fehlgeschlagener Request die aeussere `timer`-Quelle nicht beendet — sonst bricht
   * der 30-s-Poll nach dem ersten HTTP-Fehler dauerhaft ab. `null` meldet den Fehler,
   * damit der Aufrufer den letzten Stand stehen lassen kann.
   */
  poll(von?: string, bis?: string, projectIds: string[] = []): Observable<DashboardResponse | null> {
    return timer(0, POLL_INTERVAL_MS).pipe(
      switchMap(() => this.getDashboard(von, bis, projectIds).pipe(catchError(() => of(null)))),
    );
  }

  getRequirementsFunnel(projectIds: string[] = []): Observable<RequirementsFunnelResponse> {
    return this.http.get<RequirementsFunnelResponse>(`${this.apiUrl}/requirements-funnel`, {
      params: withProjectIds(new HttpParams(), projectIds),
    });
  }

  /** Wie `poll`: Fehler werden im Takt abgefangen und als `null` gemeldet. */
  pollRequirementsFunnel(projectIds: string[] = []): Observable<RequirementsFunnelResponse | null> {
    return timer(0, POLL_INTERVAL_MS).pipe(
      switchMap(() => this.getRequirementsFunnel(projectIds).pipe(catchError(() => of(null)))),
    );
  }
}

function withProjectIds(params: HttpParams, projectIds: string[]): HttpParams {
  return projectIds.reduce((acc, id) => acc.append('projectIds', id), params);
}
